import { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { getDeepUrlState, findParametersFromAllSources, logUrlDiagnostics } from '@/utils/urlDiagnostics';
import { useSessionRecovery, isExitInProgress, shouldAttemptSessionRecovery } from '@/utils/sessionRecovery';
import { isStudentRoute } from '@/utils/studentUrlHelpers';
import logger from '@/utils/logger';

export interface RobustUrlParams {
  sessionId: string | null;
  playerId: string | null;
  gameCode: string | null;
}

export interface UrlParsingDiagnostics {
  reactRouterParams: RobustUrlParams;
  manualParams: RobustUrlParams;
  rawUrl: string;
  rawSearch: string;
  parsingMethod: 'react-router' | 'manual' | 'fallback' | 'deep-analysis';
  hasDiscrepancy: boolean;
  lastUpdated: Date;
}

/**
 * Robust URL parameter extraction hook that handles React Router parsing issues
 */
export const useRobustUrlParams = (): {
  params: RobustUrlParams;
  diagnostics: UrlParsingDiagnostics;
} => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { checkAndRecover } = useSessionRecovery();
  const [diagnostics, setDiagnostics] = useState<UrlParsingDiagnostics>({
    reactRouterParams: { sessionId: null, playerId: null, gameCode: null },
    manualParams: { sessionId: null, playerId: null, gameCode: null },
    rawUrl: '',
    rawSearch: '',
    parsingMethod: 'react-router',
    hasDiscrepancy: false,
    lastUpdated: new Date()
  });

  const [finalParams, setFinalParams] = useState<RobustUrlParams>({
    sessionId: null,
    playerId: null,
    gameCode: null
  });

  useEffect(() => {
    const isStudentPath = isStudentRoute(location.pathname);

    // Skip recovery/diagnostics entirely on teacher routes to avoid hijacking their navigation
    if (!isStudentPath) {
      const neutralParams: RobustUrlParams = { sessionId: null, playerId: null, gameCode: null };
      setDiagnostics(prev => ({
        ...prev,
        reactRouterParams: neutralParams,
        manualParams: neutralParams,
        rawUrl: window.location.href,
        rawSearch: window.location.search,
        parsingMethod: 'react-router',
        hasDiscrepancy: false,
        lastUpdated: new Date()
      }));
      setFinalParams(neutralParams);
      return;
    }

    // Get comprehensive URL diagnostics
    const deepState = getDeepUrlState(location);
    const foundParams = findParametersFromAllSources(deepState);

    // Log detailed diagnostics
    if (import.meta.env.DEV) {
      logUrlDiagnostics('useRobustUrlParams');
    }

    // Method 1: React Router useSearchParams (legacy support)
    const reactRouterParams: RobustUrlParams = {
      sessionId: searchParams.get('sessionId'),
      playerId: searchParams.get('playerId'),
      gameCode: null
    };

    // Extract game code from path
    const joinGameMatch = location.pathname.match(/\/join-game\/([A-Z0-9]+)/);
    if (joinGameMatch) {
      reactRouterParams.gameCode = joinGameMatch[1];
    }

    // Method 2: Deep URL analysis (primary method)
    const deepParams: RobustUrlParams = {
      sessionId: foundParams.sessionId,
      playerId: foundParams.playerId,
      gameCode: foundParams.gameCode || reactRouterParams.gameCode
    };

    // Method 3: Manual URL parsing (fallback)
    const rawUrl = window.location.href;
    const rawSearch = window.location.search;
    const manualUrlParams = new URLSearchParams(rawSearch);

    const manualParams: RobustUrlParams = {
      sessionId: manualUrlParams.get('sessionId'),
      playerId: manualUrlParams.get('playerId'),
      gameCode: reactRouterParams.gameCode // Use same game code extraction
    };

    // Determine which method to use - prioritize deep analysis
    let finalMethod: 'react-router' | 'manual' | 'fallback' | 'deep-analysis' = 'deep-analysis';
    let chosenParams = deepParams;

    // Check for discrepancies
    const hasSessionIdDiscrepancy = reactRouterParams.sessionId !== manualParams.sessionId;
    const hasPlayerIdDiscrepancy = reactRouterParams.playerId !== manualParams.playerId;
    const hasDiscrepancy = hasSessionIdDiscrepancy || hasPlayerIdDiscrepancy;

    // If deep analysis found parameters, use those
    if (deepParams.sessionId && deepParams.playerId) {
      logger.debug('Deep URL analysis found complete parameters');
      finalMethod = 'deep-analysis';
      chosenParams = deepParams;
    }
    // Use manual parsing if React Router fails but manual parsing succeeds
    else if (!reactRouterParams.sessionId && manualParams.sessionId) {
      logger.debug('URL parsing fallback: React Router failed, using manual parsing');
      finalMethod = 'manual';
      chosenParams = manualParams;
    } else if (reactRouterParams.sessionId && !manualParams.sessionId) {
      logger.debug('URL parsing fallback: manual parsing failed, using React Router');
      finalMethod = 'react-router';
      chosenParams = reactRouterParams;
    } else if (hasDiscrepancy) {
      // Use whichever method has more complete data
      const reactRouterScore = (reactRouterParams.sessionId ? 1 : 0) + (reactRouterParams.playerId ? 1 : 0);
      const manualScore = (manualParams.sessionId ? 1 : 0) + (manualParams.playerId ? 1 : 0);

      if (manualScore > reactRouterScore) {
        logger.debug('URL parsing discrepancy detected, manual parsing has more data');
        finalMethod = 'fallback';
        chosenParams = manualParams;
      } else {
        logger.debug('URL parsing discrepancy detected, React Router has more data');
        finalMethod = 'react-router';
        chosenParams = reactRouterParams;
      }
    }

    const recoveryAllowedOnPath = shouldAttemptSessionRecovery(location.pathname);

    // If no parameters found, attempt session recovery only on active in-session routes.
    if (!chosenParams.sessionId && !chosenParams.playerId && !isExitInProgress()) {
      if (recoveryAllowedOnPath) {
        logger.debug('No URL parameters found, attempting session recovery');
        const recovered = checkAndRecover(null, null);
        if (recovered) {
          logger.debug('Session recovery initiated');
          return; // Component will re-render after recovery
        }
      } else {
        logger.debug('Skipping session recovery on entry or non-session route', {
          pathname: location.pathname
        });
      }
    } else if (isExitInProgress()) {
      logger.debug('Exit in progress, skipping URL parameter recovery');
    }

    // Update diagnostics
    const newDiagnostics: UrlParsingDiagnostics = {
      reactRouterParams,
      manualParams,
      rawUrl,
      rawSearch,
      parsingMethod: finalMethod,
      hasDiscrepancy,
      lastUpdated: new Date()
    };

    setDiagnostics(newDiagnostics);
    setFinalParams(chosenParams);

    // Debug logging
    if (import.meta.env.DEV) {
      logger.debug('URL parsing results', {
        method: finalMethod,
        params: chosenParams,
        hasDiscrepancy,
        url: rawUrl
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.pathname, location.search]); // Removed 'location' object and 'checkAndRecover' to prevent infinite loops

  return {
    params: finalParams,
    diagnostics
  };
};

/**
 * Simple hook that just returns the most reliable sessionId
 */
export const useSessionId = (): string | null => {
  const { params } = useRobustUrlParams();
  return params.sessionId;
};

/**
 * Simple hook that just returns the most reliable playerId
 */
export const usePlayerId = (): string | null => {
  const { params } = useRobustUrlParams();
  return params.playerId;
};
